import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { PageHeader, Table, Badge, TableSkeleton, EmptyState, SearchInput } from "../components/ui";

const AccountsCreation = () => {
  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState("");

  // Fetch accounts data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/personal_details`
        );
        setAccountsData(response.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch field officers on mount
  useEffect(() => {
    const fetchOfficers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/staff_details`, {
          params: { limit: 500 }
        });
        const staff = response.data.data || [];
        const officers = staff.filter(s => Number(s.roleId) === 3 || s.designation?.toLowerCase().includes("field officer"));
        setFieldOfficers(officers.length > 0 ? officers : staff);
      } catch (error) {
        console.error("Error fetching field officers:", error);
      }
    };
    fetchOfficers();
  }, []);

  const getGenderVariant = (gender) => {
    const g = String(gender).toLowerCase();
    if (g === "male" || g === "m") return "primary";
    if (g === "female" || g === "f") return "success";
    return "neutral";
  };

  const getStatusVariant = (status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "verified" || normalized === "active") return "success";
    if (normalized === "pending") return "warning";
    if (normalized === "rejected") return "danger";
    return "neutral";
  };

  const getStatusText = (status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "verified") return "Active";
    return status || "Pending";
  };

  // Client-side search and filter logic
  const filteredAccounts = accountsData.filter((account) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (account.name && account.name.toLowerCase().includes(term)) ||
      (account.customerUniqueId && account.customerUniqueId.toLowerCase().includes(term)) ||
      (account.mobileNumber && account.mobileNumber.toLowerCase().includes(term));

    const matchesStatus = selectedStatus
      ? (account.adminverification || "pending").toLowerCase() === selectedStatus.toLowerCase()
      : true;

    const matchesOfficer = selectedOfficer
      ? Number(account.fieldOfficerId) === Number(selectedOfficer)
      : true;

    return matchesSearch && matchesStatus && matchesOfficer;
  });

  return (
    <div className="p-2 bg-canvas-base min-h-screen font-sans flex flex-col gap-2">
      {/* Page Header */}
      <PageHeader
        title="Accounts Creation"
        subtitle="Review, approve, or reject new customer accounts pending verification"
        badge={
          filteredAccounts.length > 0 && (
            <Badge variant="warning" size="sm">
              {filteredAccounts.length} Pending
            </Badge>
          )
        }
      />

      {/* Filters Toolbar */}
      <div className="flex flex-row flex-wrap items-center justify-between gap-3 mb-3 w-full">
        {/* Search Bar */}
        <div className="w-48">
          <SearchInput
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-row flex-wrap items-center gap-2">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="h-9 w-32 rounded-lg border border-border bg-white text-ink-900 text-xs px-3 pr-8 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 20 20%22%3E%3Cpath stroke=%22%2394A3B8%22 stroke-width=%221.5%22 d=%22M6 8l4 4 4-4%22/%3E%3C/svg%3E')] bg-no-repeat bg-right bg-[length:16px]"
          >
            <option value="">All Status</option>
            <option value="verified">Active</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={selectedOfficer}
            onChange={(e) => setSelectedOfficer(e.target.value)}
            className="h-9 w-44 rounded-lg border border-border bg-white text-ink-900 text-xs px-3 pr-8 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 20 20%22%3E%3Cpath stroke=%22%2394A3B8%22 stroke-width=%221.5%22 d=%22M6 8l4 4 4-4%22/%3E%3C/svg%3E')] bg-no-repeat bg-right bg-[length:16px]"
          >
            <option value="">All Field Officers</option>
            {fieldOfficers.map((officer) => (
              <option key={officer.id} value={officer.id}>
                {officer.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table / Skeleton / Empty State */}
      {loading ? (
        <TableSkeleton rows={10} cols={17} />
      ) : filteredAccounts.length === 0 ? (
        <div>
          <EmptyState
            variant="search"
            title="No accounts found"
            description={searchTerm ? "Try adjusting your search terms or filters" : "No pending customer accounts found"}
            size="sm"
          />
        </div>
      ) : (
        <div>
          <Table>
            <Table.Head>
              <tr>
                <Table.HeadCell>ID</Table.HeadCell>
                <Table.HeadCell>Approve/Reject</Table.HeadCell>
                <Table.HeadCell>Customer Unique ID</Table.HeadCell>
                <Table.HeadCell align="center">Profile Image</Table.HeadCell>
                <Table.HeadCell>Field Officer ID</Table.HeadCell>
                <Table.HeadCell>Branch Name</Table.HeadCell>
                <Table.HeadCell>Center</Table.HeadCell>
                <Table.HeadCell>Name</Table.HeadCell>
                <Table.HeadCell>Gender</Table.HeadCell>
                <Table.HeadCell>Date of Birth</Table.HeadCell>
                <Table.HeadCell>Age</Table.HeadCell>
                <Table.HeadCell>Mobile Number</Table.HeadCell>
                <Table.HeadCell>Alt Mobile</Table.HeadCell>
                <Table.HeadCell>Marital Status</Table.HeadCell>
                <Table.HeadCell>Father Name</Table.HeadCell>
                <Table.HeadCell>Spouse Name</Table.HeadCell>
                <Table.HeadCell>Permanent Address</Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {filteredAccounts.map((account, index) => (
                <Table.Row key={account.id || index}>
                  <Table.Cell className="font-semibold text-ink-900">{account.id}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={getStatusVariant(account.adminverification)} dot>
                      {getStatusText(account.adminverification)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="font-data">{account.customerUniqueId || "—"}</Table.Cell>
                  <Table.Cell align="center">
                    <div className="flex items-center justify-center">
                      {account.customerProfileImage ? (
                        <img
                          src={`${API_BASE_URL}/images/${account.customerProfileImage}`}
                          alt="Customer Profile"
                          className="w-10 h-10 rounded-full object-cover border border-border-light shadow-sm"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/150";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-canvas-dark flex items-center justify-center text-ink-500 font-semibold text-xs border border-border-light shadow-sm">
                          {account.name ? account.name.charAt(0).toUpperCase() : "?"}
                        </div>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="font-data">{account.fieldOfficerId || "—"}</Table.Cell>
                  <Table.Cell className="text-ink-500">?</Table.Cell>
                  <Table.Cell className="text-ink-500">?</Table.Cell>
                  <Table.Cell className="font-medium text-ink-900">{account.name}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={getGenderVariant(account.gender)}>
                      {account.gender}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="font-data">{account.dob}</Table.Cell>
                  <Table.Cell className="font-data">{account.age}</Table.Cell>
                  <Table.Cell className="font-data">{account.mobileNumber}</Table.Cell>
                  <Table.Cell className="font-data">{account.altMobileNumber || "N/A"}</Table.Cell>
                  <Table.Cell>{account.maritalStatus}</Table.Cell>
                  <Table.Cell>{account.fatherName}</Table.Cell>
                  <Table.Cell>{account.spouseName || "N/A"}</Table.Cell>
                  <Table.Cell className="text-ink-500 truncate max-w-xs" title={`${account.permanentDoorNo || ""} ${account.permanentStreetAddress || ""} ${account.permanentVillage || ""}`}>
                    {account.permanentDoorNo || "—"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AccountsCreation;
